import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, ShieldAlert, Mail, AlertTriangle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";
const CONTACT_EMAIL = "contact@misyou.in";

export default function AntiHarassmentPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-bold text-slate-900">MISYOU</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24" data-testid="anti-harassment-page">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Anti-Harassment &amp; Misuse Policy</h1>
          <p className="text-muted-foreground">Effective Date: 17th February 2026</p>
        </div>

        <Card className="shadow-lg border-slate-100">
          <CardContent className="p-6 sm:p-10 prose prose-slate max-w-none">

            <p className="lead text-lg text-slate-600">
              MISYOU exists to help families reunite with missing loved ones. We have a
              <strong> zero-tolerance stance</strong> toward anyone who uses this platform to harass,
              intimidate, extort or defraud another user. This policy explains what we forbid, how we
              detect it, and what happens when it occurs.
            </p>

            <h2>1. What counts as harassment or misuse</h2>
            <p>The following behaviours are strictly prohibited on MISYOU and through any contact channel introduced via MISYOU:</p>
            <ul>
              <li><strong>Extortion</strong> &mdash; demanding money, goods, gift cards, OTPs, or any consideration in exchange for information about a missing person.</li>
              <li><strong>Stalking</strong> &mdash; repeated unwanted contact, surveillance, or attempts to obtain a family&apos;s home address, workplace or daily routine.</li>
              <li><strong>Intimidation &amp; threats</strong> &mdash; threats of violence, doxxing, blackmail, or threats against family members.</li>
              <li><strong>Sexual harassment</strong> &mdash; sexual advances, sexually explicit content, or unwanted comments about the missing person or family members, especially minors.</li>
              <li><strong>Hate speech &amp; discrimination</strong> &mdash; abuse based on caste, religion, gender, sexual orientation, disability, ethnicity, nationality or language.</li>
              <li><strong>Impersonation</strong> &mdash; pretending to be police, hospital staff, NGO workers, or another user.</li>
              <li><strong>Fake or duplicate listings</strong> &mdash; posting fabricated cases, recycled photos, or AI-generated faces.</li>
              <li><strong>Spam &amp; promotional abuse</strong> &mdash; posting unrelated commercial messages, links, or political content.</li>
              <li><strong>Privacy violations</strong> &mdash; sharing another user&apos;s phone, email, address or photos without consent; harvesting MISYOU contact details for unrelated uses.</li>
              <li><strong>Platform manipulation</strong> &mdash; automated scraping, bot submissions, mass account creation, ban evasion, or any attempt to disrupt service.</li>
            </ul>

            <h2>2. How we detect misuse</h2>
            <p>MISYOU runs continuous automated and manual safeguards:</p>
            <ul>
              <li>Scam-keyword detection on every case, sighting and abuse report.</li>
              <li>Per-IP and per-account rate limits on case creation, sighting submission and abuse reporting.</li>
              <li>Required <strong>Math CAPTCHA</strong> on sighting and abuse-report forms.</li>
              <li>Public contact phone &amp; email of families are hidden &mdash; outsiders must use the in-app Submit-a-Sighting flow.</li>
              <li>Free-text fields automatically reject phone numbers to prevent scammer harvesting.</li>
              <li>Repeat-offender phone &amp; email pattern detection in the admin dashboard.</li>
              <li>Trusted moderators review flagged content within 48 hours.</li>
            </ul>

            <h2>3. Consequences for offenders</h2>
            <p>Depending on severity and history we may take one or more of the following actions:</p>
            <ul>
              <li>Content removed and case/sighting hidden from public view.</li>
              <li>Warning issued; further violations trigger automatic suspension.</li>
              <li><strong>Immediate and permanent ban</strong> of the offending account and the device/IP used.</li>
              <li>Refusal of all future registrations from the same identity, email, phone or device fingerprint.</li>
              <li>Forwarding of preserved evidence (account details, IP, user-agent, timestamps, suspect phone &amp; email, message history) to local police and to <a href="https://cybercrime.gov.in/" target="_blank" rel="noreferrer">cybercrime.gov.in</a>.</li>
              <li>Civil and/or criminal proceedings under applicable Indian law (see Section 4).</li>
            </ul>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-6" data-testid="anti-harassment-evidence-clause">
              <h3 className="flex items-center gap-2 text-red-800 mt-0">
                <AlertTriangle className="w-5 h-5" />
                We keep evidence logs
              </h3>
              <p className="text-red-700 mb-2">
                For every safety-relevant action on MISYOU we retain:
              </p>
              <ul className="text-red-700 mb-0">
                <li>Account ID, email, phone (if provided), and consent timestamps.</li>
                <li>IP address and user-agent / device fingerprint of the request.</li>
                <li>Server-side timestamps of logins, case creations, sightings, abuse reports and moderator actions.</li>
                <li>Reported scammer phone numbers, emails and the verbatim abuse-report text.</li>
                <li>Photo and content hashes to spot recycled or AI-generated submissions.</li>
              </ul>
              <p className="text-red-700 mt-2 mb-0">
                These logs are strictly internal and admin-only. We do <strong>not</strong> sell or share them
                with advertisers. We release them only to families under court order, or to police /
                cybercrime authorities under a lawful request &mdash; <strong>and we will proactively cooperate
                with any investigation into harassment or extortion of a MISYOU family.</strong>
              </p>
            </div>

            <h2>4. Applicable Indian law</h2>
            <p>Misuse of MISYOU may attract proceedings under (non-exhaustive):</p>
            <ul>
              <li>Bharatiya Nyaya Sanhita, 2023 / Indian Penal Code &mdash; extortion (IPC 384&ndash;389), criminal intimidation (IPC 503, 506&ndash;507), false information to public servant (IPC 182), false charge of offence (IPC 211), defamation (IPC 499), outraging modesty (IPC 354A&ndash;D), stalking (IPC 354D), cheating (IPC 415, 420).</li>
              <li>Information Technology Act, 2000 &mdash; identity theft (Sec. 66C), cheating by personation using computer (Sec. 66D), publishing obscene material (Sec. 67), violation of privacy (Sec. 66E), abetment (Sec. 84B).</li>
              <li>Protection of Children from Sexual Offences (POCSO) Act, 2012 &mdash; where minors are involved.</li>
              <li>Digital Personal Data Protection Act, 2023 &mdash; for unlawful processing of personal data.</li>
            </ul>

            <h2>5. How to report harassment or misuse</h2>
            <p>If you experience or witness any of the behaviours above:</p>
            <ol>
              <li>Open the relevant case page and tap <strong>&ldquo;Report a scam / abuse&rdquo;</strong> &mdash; this is the fastest route and writes directly to our moderation queue.</li>
              <li>If you receive a phone call or message off-platform, take screenshots and note the date, time and phone number, then file the abuse report and attach the suspect&apos;s number / email.</li>
              <li>Do <strong>not</strong> pay anyone, share OTPs, or travel alone to meet a tipster. <strong>Always involve your local police station first.</strong></li>
              <li>For urgent threats or extortion attempts, file an immediate complaint at <a href="https://cybercrime.gov.in/" target="_blank" rel="noreferrer">cybercrime.gov.in</a> or dial <strong>1930</strong> (India cyber-crime helpline).</li>
              <li>Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you need a formal copy of evidence logs for police.</li>
            </ol>

            <h2>6. Protections for the reporter</h2>
            <ul>
              <li>Abuse reports may be filed anonymously.</li>
              <li>We will not reveal a reporter&apos;s identity to the accused user.</li>
              <li>Retaliation against a reporter is itself a violation and is treated with the same severity as the original offence.</li>
            </ul>

            <h2>7. Appeals</h2>
            <p>
              If your content was removed or your account was suspended and you believe this was in error,
              you may appeal once within <strong>30 days</strong> by emailing
              {' '}<a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> from the registered email
              address, with a clear explanation. Decisions on extortion, threats, and minor-safety
              violations are <strong>final and not appealable</strong>.
            </p>

            <h2>8. Updates to this policy</h2>
            <p>
              We may update this Anti-Harassment &amp; Misuse Policy as new threats emerge. Material
              changes will be announced in-app or via email. Continued use of MISYOU after an update
              constitutes acceptance of the revised policy.
            </p>

            <h2>9. Contact</h2>
            <div className="bg-slate-50 p-4 rounded-lg mt-4">
              <p className="flex items-center gap-2 mb-2">
                <Mail className="w-5 h-5 text-primary" />
                <strong>Trust &amp; Safety:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </p>
              <p className="mb-2"><strong>Phone:</strong> +91 81464 99739</p>
              <p className="mb-0"><strong>Jurisdiction:</strong> Bathinda, Punjab, India</p>
            </div>

          </CardContent>
        </Card>

        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/privacy">
            <Button variant="outline">Privacy Policy</Button>
          </Link>
          <Link to="/terms">
            <Button variant="outline">Terms &amp; Conditions</Button>
          </Link>
          <Link to="/disclaimer">
            <Button variant="outline">Disclaimer</Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
